<template>
  <div class="comprehensive-analysis">
    <h1>Comprehensive Analysis</h1>
    
    <!-- Debug controls -->
    <div class="debug-controls">
      <button @click="testAPIEndpoints" class="debug-button">Test API Endpoints</button>
    </div>
    
    <!-- Simple loading state -->
    <div v-if="loading" class="loading-state">
      Loading comprehensive checks...
    </div>
    
    <!-- Error state -->
    <div v-if="error" class="error-state">
      <p>{{ error }}</p>
      <button @click="fetchChecks">Retry</button>
    </div>
    
    <!-- List of available comprehensive checks -->
    <div v-if="!loading && !error && checkDefinitions.length > 0" class="checks-list">
      <h2>Available Checks</h2>
      <ul>
        <li v-for="check in checkDefinitions" :key="check.id" class="check-item">
          <div class="check-info">
            <h3>{{ check.name }}</h3>
            <p>{{ check.description }}</p>
            <p class="document-types">
              Applicable documents: {{ formatDocumentTypes(check.document_types) }}
            </p>
          </div>
          <button @click="runCheck(check)" class="run-button">Run Analysis</button>
        </li>
      </ul>
    </div>
    
    <!-- No checks available state -->
    <div v-if="!loading && !error && checkDefinitions.length === 0" class="empty-state">
      <p>No comprehensive checks available for this project.</p>
    </div>
    
    <!-- Run check dialog -->
    <div v-if="showRunDialog" class="dialog-overlay">
      <div class="dialog">
        <h2>Run {{ selectedCheck?.name }}</h2>
        <p>Select a document to analyze:</p>
        
        <div v-if="loadingDocuments" class="loading-state">
          Loading documents...
        </div>
        
        <div v-else-if="documents.length === 0" class="empty-state">
          <p>No documents available. Please upload documents first.</p>
        </div>
        
        <div v-else class="document-list">
          <div 
            v-for="doc in documents" 
            :key="doc.id" 
            class="document-item"
            :class="{ 'selected': selectedDocuments.includes(doc.id) }"
            @click="toggleDocument(doc.id)"
          >
            <span class="doc-name">{{ doc.name }}</span>
            <span class="doc-type">{{ doc.document_type }}</span>
          </div>
        </div>
        
        <div class="dialog-actions">
          <button @click="closeRunDialog" class="cancel-button">Cancel</button>
          <button 
            @click="confirmRunCheck" 
            class="confirm-button"
            :disabled="selectedDocuments.length === 0 || runningCheck"
          >
            {{ runningCheck ? 'Running...' : 'Run Analysis' }}
          </button>
        </div>
      </div>
    </div>
    
    <!-- Recent check runs -->
    <div v-if="!loading && checkRuns.length > 0" class="check-runs">
      <h2>Recent Analysis Runs</h2>
      <ul>
        <li v-for="run in checkRuns" :key="run.id" class="run-item">
          <div class="run-info">
            <span class="run-id">ID: {{ run.id }}</span>
            <span :class="['run-status', run.status.toLowerCase()]">{{ run.status }}</span>
            <span class="run-date">{{ formatDate(run.created_at) }}</span>
          </div>
          <div class="run-document">
            Document: {{ documentNames[run.document] || 'Unknown document' }}
          </div>
          <div class="run-actions">
            <button 
              v-if="run.status === 'COMPLETED'" 
              @click="viewResults(run)" 
              class="view-button"
            >
              View Results
            </button>
          </div>
        </li>
      </ul>
    </div>
    
    <!-- Results dialog -->
    <div v-if="showResultsDialog" class="dialog-overlay">
      <div class="dialog results-dialog">
        <div class="dialog-header">
          <h2>Analysis Results</h2>
          <button @click="closeResultsDialog" class="close-button">&times;</button>
        </div>
        
        <div v-if="loadingResults" class="loading-state">
          Loading results...
        </div>
        
        <div v-else class="results-content">
          <div class="run-summary">
            <p>Document: <strong>{{ documentNames[selectedRun?.document] || 'Unknown document' }}</strong></p>
            <p>Status: <span :class="['run-status', selectedRun?.status.toLowerCase()]">{{ selectedRun?.status }}</span></p>
            <p>Date: {{ formatDate(selectedRun?.created_at) }}</p>
          </div>
          
          <div v-if="requirementResults.length > 0" class="requirements-list">
            <h3>Requirements</h3>
            <div 
              v-for="result in requirementResults" 
              :key="result.id" 
              :class="['requirement-item', getResultPassed(result) ? 'passed' : 'failed']"
            >
              <div class="requirement-header">
                <span class="requirement-name">{{ result.requirement_name }}</span>
                <span :class="['requirement-status', getResultBadgeClass(result)]" :title="getResultTooltip(result)">
                  {{ getResultDisplayText(result) }}
                </span>
              </div>
              <div class="requirement-description">{{ result.description }}</div>
              <div v-if="getEvidenceText(result) && getEvidenceText(result) !== '--'" class="requirement-evidence">
                <strong>Evidence:</strong> {{ getEvidenceText(result) }}
                <div v-if="hasEnhancedData(result)" class="enhanced-details">
                  <small v-if="getConfidence(result)" class="confidence-text">
                    (Confidence: {{ Math.round(getConfidence(result) * 100) }}%)
                  </small>
                  <div v-if="getReasoning(result)" class="reasoning-text">
                    <em>{{ getReasoning(result) }}</em>
                  </div>
                  <div v-if="getDeficiencies(result).length > 0" class="deficiencies-text">
                    <strong>Issues:</strong>
                    <ul>
                      <li v-for="deficiency in getDeficiencies(result)" :key="deficiency">
                        {{ deficiency }}
                      </li>
                    </ul>
                  </div>
                  <div v-if="getSuggestions(result).length > 0" class="suggestions-text">
                    <strong>Recommendations:</strong>
                    <ul>
                      <li v-for="suggestion in getSuggestions(result)" :key="suggestion">
                        {{ suggestion }}
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div v-else class="empty-state">
            <p>No requirement results available.</p>
          </div>
        </div>
        
        <div class="dialog-actions">
          <button @click="closeResultsDialog" class="close-button-text">Close</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';

export default {
  name: 'ComprehensiveAnalysis',
  props: {
    projectId: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      loading: false,
      error: null,
      checkDefinitions: [],
      checkRuns: [],
      documents: [],
      documentNames: {}, // Map of document IDs to names for quick lookup
      showRunDialog: false,
      showResultsDialog: false,
      selectedCheck: null,
      selectedRun: null,
      selectedDocuments: [],
      requirementResults: [],
      loadingDocuments: false,
      loadingResults: false,
      runningCheck: false
    }
  },
  mounted() {
    this.fetchChecks();
    this.fetchCheckRuns();
  },
  methods: {
    // Test available API endpoints
    async testAPIEndpoints() {
      console.log('Testing API endpoints...');
      
      try {
        console.log('Testing endpoint 1: /comprehensive-check-definitions/');
        const test1 = await api.get('/comprehensive-check-definitions/');
        console.log('Endpoint 1 result:', test1);
      } catch (err) {
        console.error('Endpoint 1 failed:', err);
      }
      
      try {
        console.log('Testing endpoint 2: /comprehensive-check-runs/');
        const test2 = await api.get('/comprehensive-check-runs/');
        console.log('Endpoint 2 result:', test2);
      } catch (err) {
        console.error('Endpoint 2 failed:', err);
      }
      
      try {
        console.log(`Testing endpoint 3: /projects/${this.projectId}/comprehensive-check-runs/`);
        const test3 = await api.get(`/projects/${this.projectId}/comprehensive-check-runs/`);
        console.log('Endpoint 3 result:', test3);
      } catch (err) {
        console.error('Endpoint 3 failed:', err);
      }
      
      try {
        console.log(`Testing endpoint 4: /comprehensive-check-definitions/?project_type=${this.projectId}`);
        const test4 = await api.get(`/comprehensive-check-definitions/?project_type=${this.projectId}`);
        console.log('Endpoint 4 result:', test4);
      } catch (err) {
        console.error('Endpoint 4 failed:', err);
      }
    },
    
    // Fetch comprehensive check definitions
    async fetchChecks() {
      this.loading = true;
      this.error = null;
      try {
        const response = await api.get(`/comprehensive-check-definitions/?project_type=${this.projectId}`);
        this.checkDefinitions = response.data;
      } catch (err) {
        console.error('Error fetching check definitions:', err);
        this.error = 'Failed to load comprehensive checks. Please try again.';
      } finally {
        this.loading = false;
      }
    },
    
    // Fetch recent check runs
    async fetchCheckRuns() {
      try {
        const response = await api.get(`/projects/${this.projectId}/comprehensive-check-runs/`);
        this.checkRuns = response.data;
        
        // Build a map of document IDs to names
        if (response.data.length > 0) {
          // Get unique document IDs
          const documentIds = [...new Set(response.data.map(run => run.document))];
          
          // Fetch document details if we have runs
          if (documentIds.length > 0) {
            this.fetchDocumentNames(documentIds);
          }
        }
      } catch (err) {
        console.error('Error fetching check runs:', err);
        // Don't show an error for this, just log it
      }
    },
    
    // Fetch document names for display
    async fetchDocumentNames(documentIds) {
      try {
        console.log('Fetching document names for IDs:', documentIds);
        // We'll fetch all documents and then filter
        const response = await api.get(`/projects/${this.projectId}/documents/`);
        const documents = response.data;
        console.log('Fetched documents:', documents);
        
        // Create a map of document IDs to names
        const documentMap = {};
        documents.forEach(doc => {
          if (documentIds.includes(doc.id)) {
            documentMap[doc.id] = doc.name || `Document ${doc.id}`;
          }
        });
        
        console.log('Document name map:', documentMap);
        this.documentNames = documentMap;
      } catch (err) {
        console.error('Error fetching document names:', err);
      }
    },
    
    // Run a comprehensive check
    runCheck(check) {
      this.selectedCheck = check;
      this.selectedDocuments = [];
      this.showRunDialog = true;
      this.fetchDocuments();
    },
    
    // Fetch documents for the run dialog
    async fetchDocuments() {
      this.loadingDocuments = true;
      try {
        const response = await api.get(`/projects/${this.projectId}/documents/`);
        const data = response.data;
        
        // If check has document_types, filter documents by those types
        if (this.selectedCheck && this.selectedCheck.document_types && this.selectedCheck.document_types.length > 0) {
          this.documents = data.filter(doc => 
            this.selectedCheck.document_types.includes(doc.document_type)
          );
        } else {
          this.documents = data;
        }
        
        // Also update document names map
        const documentMap = {};
        data.forEach(doc => {
          documentMap[doc.id] = doc.name;
        });
        this.documentNames = { ...this.documentNames, ...documentMap };
      } catch (err) {
        console.error('Error fetching documents:', err);
        this.documents = [];
      } finally {
        this.loadingDocuments = false;
      }
    },
    
    // Toggle document selection
    toggleDocument(docId) {
      const index = this.selectedDocuments.indexOf(docId);
      if (index === -1) {
        this.selectedDocuments.push(docId);
      } else {
        this.selectedDocuments.splice(index, 1);
      }
    },
    
    // Close the run dialog
    closeRunDialog() {
      this.showRunDialog = false;
      this.selectedCheck = null;
      this.selectedDocuments = [];
    },
    
    // Confirm and execute the check run
    async confirmRunCheck() {
      if (this.selectedDocuments.length === 0 || !this.selectedCheck) {
        return;
      }
      
      this.runningCheck = true;
      
      try {
        // For each selected document, run the check
        for (const docId of this.selectedDocuments) {
          await api.post(`/comprehensive-check-runs/`, {
            check_definition: this.selectedCheck.id,
            document: docId,
            project_id: this.projectId // Include the project ID for context
          });
        }
        
        // Close dialog and refresh runs
        this.closeRunDialog();
        this.fetchCheckRuns();
        
      } catch (err) {
        console.error('Error running check:', err);
        alert('Failed to run analysis. Please try again.');
      } finally {
        this.runningCheck = false;
      }
    },
    
    // View results for a check run
    async viewResults(run) {
      this.selectedRun = run;
      this.showResultsDialog = true;
      this.requirementResults = [];
      await this.fetchRequirementResults(run.id);
    },
    
    // Fetch requirement results for a check run
    async fetchRequirementResults(runId) {
      this.loadingResults = true;
      try {
        const response = await api.get(`/requirement-results/`, {
          params: {
            check_run_id: runId
          }
        });
        this.requirementResults = response.data;
      } catch (err) {
        console.error('Error fetching requirement results:', err);
      } finally {
        this.loadingResults = false;
      }
    },
    
    // Close the results dialog
    closeResultsDialog() {
      this.showResultsDialog = false;
      this.selectedRun = null;
      this.requirementResults = [];
    },
    
    // Format document types for display
    formatDocumentTypes(types) {
      if (!types || types.length === 0) {
        return 'All document types';
      }
      return types.join(', ');
    },
    
    // Format date for display
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleString();
    },

    // Enhanced Result Parsing Methods for Enhanced Prompts
    getResultPassed(result) {
      if (!result) return false;
      
      // Handle new enhanced prompt format
      if (result.evidence && typeof result.evidence === 'string') {
        try {
          const enhancedData = JSON.parse(result.evidence);
          if (enhancedData.result) {
            return enhancedData.result.toUpperCase() === 'PASS';
          }
        } catch (e) {
          // Not JSON, fall through to legacy format
        }
      }
      
      // Legacy format fallback
      return result.passed === true;
    },

    getResultDisplayText(result) {
      if (!result) return 'PENDING';
      
      // Handle new enhanced prompt format
      if (result.evidence && typeof result.evidence === 'string') {
        try {
          const enhancedData = JSON.parse(result.evidence);
          if (enhancedData.result) {
            return enhancedData.result.toUpperCase();
          }
        } catch (e) {
          // Not JSON, fall through to legacy format
        }
      }
      
      // Legacy format fallback
      return result.passed ? 'PASSED' : 'FAILED';
    },

    getResultBadgeClass(result) {
      if (!result) return 'pending';
      
      // Handle new enhanced prompt format
      if (result.evidence && typeof result.evidence === 'string') {
        try {
          const enhancedData = JSON.parse(result.evidence);
          if (enhancedData.result) {
            const resultValue = enhancedData.result.toUpperCase();
            switch (resultValue) {
              case 'PASS': return 'passed';
              case 'FAIL': return 'failed';
              case 'PARTIAL': return 'partial';
              case 'UNCLEAR': return 'unclear';
              default: return 'pending';
            }
          }
        } catch (e) {
          // Not JSON, fall through to legacy format
        }
      }
      
      // Legacy format fallback
      return result.passed ? 'passed' : 'failed';
    },

    getResultTooltip(result) {
      if (!result) return 'Analysis pending';
      
      // Handle new enhanced prompt format
      if (result.evidence && typeof result.evidence === 'string') {
        try {
          const enhancedData = JSON.parse(result.evidence);
          if (enhancedData.confidence) {
            const confidence = Math.round(enhancedData.confidence * 100);
            return `Result: ${enhancedData.result || 'Unknown'} (${confidence}% confidence)`;
          }
        } catch (e) {
          // Not JSON, fall through to legacy format
        }
      }
      
      // Legacy format fallback
      return result.passed ? 'Requirement passed' : 'Requirement failed';
    },

    getEvidenceText(result) {
      if (!result) return '--';
      
      // Handle new enhanced prompt format
      if (result.evidence && typeof result.evidence === 'string') {
        try {
          const enhancedData = JSON.parse(result.evidence);
          if (enhancedData.evidence) {
            return enhancedData.evidence;
          }
        } catch (e) {
          // Not JSON, treat as simple text evidence
          return result.evidence;
        }
      }
      
      // Legacy format or simple evidence
      return result.evidence || '--';
    },

    hasEnhancedData(result) {
      if (!result || !result.evidence || typeof result.evidence !== 'string') return false;
      
      try {
        const enhancedData = JSON.parse(result.evidence);
        return enhancedData.result || enhancedData.confidence || enhancedData.reasoning;
      } catch (e) {
        return false;
      }
    },

    getConfidence(result) {
      if (!result || !result.evidence || typeof result.evidence !== 'string') return null;
      
      try {
        const enhancedData = JSON.parse(result.evidence);
        return enhancedData.confidence || null;
      } catch (e) {
        return null;
      }
    },

    getReasoning(result) {
      if (!result || !result.evidence || typeof result.evidence !== 'string') return '';
      
      try {
        const enhancedData = JSON.parse(result.evidence);
        return enhancedData.reasoning || '';
      } catch (e) {
        return '';
      }
    },

    getDeficiencies(result) {
      if (!result || !result.evidence || typeof result.evidence !== 'string') return [];
      
      try {
        const enhancedData = JSON.parse(result.evidence);
        return enhancedData.deficiencies || [];
      } catch (e) {
        return [];
      }
    },

    getSuggestions(result) {
      if (!result || !result.evidence || typeof result.evidence !== 'string') return [];
      
      try {
        const enhancedData = JSON.parse(result.evidence);
        return enhancedData.suggestions || [];
      } catch (e) {
        return [];
      }
    }
  }
}
</script>

<style scoped>
.comprehensive-analysis {
  padding: 20px;
  max-width: 1000px;
  margin: 0 auto;
}

h1 {
  margin-bottom: 20px;
}

.loading-state {
  padding: 20px;
  text-align: center;
  color: #666;
}

.error-state {
  padding: 20px;
  background-color: #fff5f5;
  border: 1px solid #feb2b2;
  border-radius: 4px;
  margin-bottom: 20px;
}

.empty-state {
  padding: 20px;
  text-align: center;
  background-color: #f7fafc;
  border-radius: 4px;
  margin-bottom: 20px;
}

.checks-list, .check-runs {
  margin-top: 20px;
}

.check-item, .run-item {
  background-color: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  padding: 15px;
  margin-bottom: 15px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.check-info h3 {
  margin: 0 0 5px 0;
}

.document-types {
  font-size: 0.9em;
  color: #718096;
}

.run-button, .view-button {
  align-self: flex-start;
  background-color: #4299e1;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
}

.run-button:hover, .view-button:hover {
  background-color: #3182ce;
}

.run-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.run-status {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.85em;
}

.run-status.completed {
  background-color: #c6f6d5;
  color: #22543d;
}

.run-status.pending, .run-status.in_progress {
  background-color: #bee3f8;
  color: #2c5282;
}

.run-status.failed {
  background-color: #fed7d7;
  color: #9b2c2c;
}

.run-date {
  font-size: 0.85em;
  color: #718096;
}

.run-document {
  font-size: 0.9em;
}

/* Dialog styles */
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.dialog {
  background-color: white;
  border-radius: 8px;
  padding: 20px;
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  overflow-y: auto;
}

.results-dialog {
  max-width: 700px;
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.close-button {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
}

.document-list {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  margin: 15px 0;
}

.document-item {
  padding: 10px 15px;
  border-bottom: 1px solid #e2e8f0;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
}

.document-item:last-child {
  border-bottom: none;
}

.document-item:hover {
  background-color: #f7fafc;
}

.document-item.selected {
  background-color: #ebf8ff;
}

.doc-type {
  color: #718096;
  font-size: 0.85em;
}

.dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

.cancel-button {
  background-color: #e2e8f0;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
}

.confirm-button {
  background-color: #4299e1;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
}

.confirm-button:disabled {
  background-color: #a0aec0;
  cursor: not-allowed;
}

.run-summary {
  background-color: #f7fafc;
  padding: 15px;
  border-radius: 4px;
  margin-bottom: 20px;
}

.requirements-list {
  margin-top: 20px;
}

.requirement-item {
  background-color: #f7fafc;
  border-radius: 4px;
  padding: 15px;
  margin-bottom: 15px;
  border-left: 4px solid #a0aec0;
}

.requirement-item.passed {
  border-left-color: #48bb78;
}

.requirement-item.failed {
  border-left-color: #f56565;
}

.requirement-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
}

.requirement-name {
  font-weight: bold;
}

.requirement-status {
  font-weight: 500;
}

.requirement-status.passed {
  color: #2f855a;
}

.requirement-status.failed {
  color: #c53030;
}

.requirement-status.partial {
  color: #d69e2e;
}

.requirement-status.unclear {
  color: #805ad5;
}

.requirement-status.pending {
  color: #3182ce;
}

.requirement-description {
  margin-bottom: 10px;
  color: #4a5568;
}

.requirement-evidence {
  font-size: 0.9em;
  color: #4a5568;
  background-color: #edf2f7;
  padding: 10px;
  border-radius: 4px;
}

.enhanced-details {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #cbd5e0;
}

.confidence-text {
  color: #718096;
  font-style: italic;
}

.reasoning-text {
  margin-top: 4px;
  color: #4a5568;
}

.deficiencies-text {
  margin-top: 4px;
  color: #e53e3e;
}

.suggestions-text {
  margin-top: 4px;
  color: #38a169;
}

.deficiencies-text ul,
.suggestions-text ul {
  margin-top: 2px;
  margin-bottom: 0;
  padding-left: 1rem;
}

.deficiencies-text li,
.suggestions-text li {
  margin-bottom: 0.25rem;
}

.close-button-text {
  background: none;
  border: none;
  color: #4a5568;
  text-decoration: underline;
  cursor: pointer;
}

.debug-controls {
  margin-bottom: 15px;
  padding: 10px;
  background-color: #f0f9ff;
  border: 1px dashed #90cdf4;
  border-radius: 4px;
}

.debug-button {
  background-color: #63b3ed;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
}

.debug-button:hover {
  background-color: #4299e1;
}
</style> 